import {
  firestoreDateParser,
  Product,
  OptionConfiguration,
  ProductOptionItem,
  ProductOptionRangeType,
  SUPPORTED_LANGUAGES,
  getPropInSelectedLanguage,
  Feature,
} from '@pedix-workspace/utils';
import { nanoid } from 'nanoid';
import { FirestoreNestedConverter, withNestedConverter } from './utils';

export type ProductConverterParams = {
  nestedConverter?: FirestoreNestedConverter<Product>;
  language?: SUPPORTED_LANGUAGES;
  features?: Feature[];
};

export const getProductConverter = ({
  language,
  features,
  nestedConverter,
}: ProductConverterParams = {}) => {
  const featuresSet = features ? new Set(features) : undefined;

  return withNestedConverter<Product>({
    toFirestore(productData: Product) {
      return productData;
    },
    fromFirestore(snapshot) {
      return convertProductData(snapshot.id, <Product>snapshot.data(), language, featuresSet);
    },
  })(nestedConverter);
};

export const convertProductData = (
  productId: string,
  productData: Partial<Product>,
  language: SUPPORTED_LANGUAGES = 'es',
  features?: Set<Feature>,
): Product => {
  // Maps old single-image to images array
  const images = getProductImagesAndCleanOldData(productData.images, productData['image']);

  // Maps old presentations array format
  const presentations = getPresentations(productId, productData.presentations);

  const name = getPropInSelectedLanguage(productData, 'name', language, true);
  const description = getPropInSelectedLanguage(productData, 'description', language, true);
  const linkedProductTagIds = productData.linkedProductTagIds || [];
  const primaryTagId = productData.primaryTagId || undefined;

  const convertedData = {
    ...productData,
    id: productId,
    name,
    linkedProductTagIds,
    primaryTagId,
    description,
    images,
    presentations,
    sortOrder: productData.sortOrder || 0,
    hidden: productData.hidden || false,
    options: getProductOptions(productId, productData.options),
    added: firestoreDateParser(productData.added),
    updated: firestoreDateParser(productData.updated),
  };

  // Removes legacy props
  delete convertedData['image'];
  delete convertedData['renditions'];
  delete convertedData['withoutStock'];
  // Stock ya no vive en el producto (vive en `productStock`); limpiamos el campo legacy al leer/convertir.
  delete convertedData['stockQty'];

  // Fixes outdated prices entries for products with presentations
  if (convertedData.presentations?.items?.length > 0) {
    convertedData.price = null;
    convertedData.priceDiscount = null;
  }

  // If provided with available features, performs data validation/truncation
  if (features && features.size > 0) {
    if (!features.has('PRODUCT_PRESENTATIONS')) {
      convertedData.presentations = null;
    }
    if (!features.has('PRODUCT_OPTIONS')) {
      convertedData.options = [];
    }
    if (!features.has('PRODUCT_GROUPS')) {
      convertedData.groupName = undefined;
    }
    if (!features.has('PRODUCT_TAGS')) {
      convertedData.primaryTagId = null;
      convertedData.linkedProductTagIds = [];
    }
    if (convertedData.images) {
      const maxImages = getMaxGalleryImages(convertedData.isOptionItem, features);

      convertedData.images = convertedData.images.slice(0, maxImages);
    }
  }

  return <Product>convertedData;
};

export function getMaxGalleryImages(
  isOptionItem: boolean,
  features: Set<Feature> | Array<Feature>,
): number {
  if (isOptionItem) {
    if (
      Array.isArray(features)
        ? features.includes('PRODUCT_ITEM_IMAGES')
        : features.has('PRODUCT_ITEM_IMAGES')
    ) {
      return 1;
    } else {
      return 0;
    }
  } else {
    if (
      Array.isArray(features)
        ? features.includes('IMAGE_GALLERY_EXTENDED')
        : features.has('IMAGE_GALLERY_EXTENDED')
    ) {
      return 10;
    } else if (
      Array.isArray(features) ? features.includes('IMAGE_GALLERY') : features.has('IMAGE_GALLERY')
    ) {
      return 3;
    } else {
      return 1;
    }
  }
}

export const getProductImagesAndCleanOldData = (
  images?: string[],
  legacyImage?: string,
): string[] => {
  if (Array.isArray(images)) {
    // NOTE: images array should not contain nullish values; but there were some null-images related errors, so there might be some corrupted records out there
    return images.filter(image => !!image);
  }
  if (legacyImage) {
    return [legacyImage];
  }
  return [];
};

export const getPresentations = (
  productId: string,
  presentations?: Product['presentations'],
): Product['presentations'] => {
  if (!presentations) {
    return null;
  }
  let updatedPresentations = presentations;

  // Check for legacy (deprecated) presentations
  if (Array.isArray(updatedPresentations)) {
    updatedPresentations = transformProductPresentationIntoProductOption(updatedPresentations);
  }
  updatedPresentations = prependOptionConfigurationWithProductId(productId, updatedPresentations);

  if (updatedPresentations.rangeType === ProductOptionRangeType.ANY) {
    delete updatedPresentations.max;
  }

  updatedPresentations.items.forEach(presentation => {
    // Limpia props legacy (el stock ya no vive en el item; vive en `productStock`).
    delete presentation['stock'];
    delete presentation['stockQty'];
  });

  return updatedPresentations;
};

/**
 * Resolves the normalized id of an option/presentation item: prepends `${productId}-` when the raw
 * id isn't already prefixed, and mints a deterministic `${productId}-${index}` when the raw id is
 * null/empty (null-guard — evita el crash `null.startsWith`).
 */
export const resolveNormalizedItemId = (
  productId: string,
  rawId: string | null | undefined,
  index: number,
): string => {
  const prefix = `${productId}-`;

  if (!rawId) {
    return `${prefix}${index}`;
  }
  return rawId.startsWith(prefix) ? rawId : `${prefix}${rawId}`;
};

/**
 * Genera un id NUEVO de option/presentación en el formato normalizado (`${productId}-…`) — el mismo
 * que produce `resolveNormalizedItemId` al leer. Usarlo al crear items (presentaciones/opciones) y al
 * clonar, para que el id sea estable entre write y read (clave de `productStock`) y no se re-prefije.
 */
export const mintOptionItemId = (productId: string): string =>
  resolveNormalizedItemId(productId, nanoid(), 0);

/**
 * Normalizes every item id in place and guarantees uniqueness within the owner, breaking ties
 * DETERMINISTICALLY by index (`${id}-${index}`) — corre en cada lectura, así que el desempate no
 * puede ser aleatorio: el mismo array siempre produce los mismos ids (estables para keyear stock).
 */
export const normalizeAndDedupeItemIds = (productId: string, items: ProductOptionItem[]): void => {
  const seen = new Set<string>();

  items.forEach((item, index) => {
    let id = resolveNormalizedItemId(productId, item.id, index);

    while (seen.has(id)) {
      id = `${id}-${index}`;
    }
    seen.add(id);
    item.id = id;
  });
};

export const prependOptionConfigurationWithProductId = (
  productId: string,
  option?: OptionConfiguration,
) => {
  if (!option) {
    return undefined;
  }
  option.id = resolveNormalizedItemId(productId, option.id, 0);

  if (option.items) {
    normalizeAndDedupeItemIds(productId, option.items);
  }

  return option;
};

export const transformProductPresentationIntoProductOption = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  presentations: any[],
): OptionConfiguration => {
  return {
    id: 'presentations',
    name: 'Presentaciones',
    rangeType: ProductOptionRangeType.FIXED,
    min: 1,
    displayItemQuantities: false,
    sortOrder: 0,
    items: presentations.map<ProductOptionItem>((presentation, index) => {
      return {
        id: `presentation-${index}`,
        ...presentation,
        sortOrder: index,
      };
    }),
  };
};

// Maps legacy product options
export const getProductOptions = (
  productId: string,
  options: Product['options'],
): Product['options'] => {
  if (!Array.isArray(options)) {
    return [];
  }

  return filterInvalidProductOptions(options)
    .map(option => prependOptionConfigurationWithProductId(productId, option))
    .map((option, optionIndex) => ({
      ...option,
      type: option.type || 'customization',
      sortOrder: optionIndex,
      min: option.rangeType === 'any' ? null : option.min,
      max: option.rangeType === 'any' ? null : option.max,
      items: option.items.map((item, itemIndex) => {
        // Limpia props legacy (el stock ya no vive en el item; vive en `productStock`).
        delete item['stock'];
        delete item['stockQty'];

        return {
          ...item,
          sortOrder: itemIndex,
          quantity: item.quantity || 0,
        };
      }),
    }));
};

// Filters-out legacy/deprecated options
const filterInvalidProductOptions = (options?: OptionConfiguration[]): OptionConfiguration[] => {
  // For each option, if that option has at least one item of type 'string', then we filter the whole option away
  return options.filter(option => !option.items.some(item => typeof item === 'string'));
};

export const productHasLinkedOptions = (product: Pick<Product, 'linkedOptionIds'>): boolean => {
  return Array.isArray(product.linkedOptionIds) && product.linkedOptionIds.length > 0;
};

export const productHasLinkedProductTags = (
  product: Pick<Product, 'linkedProductTagIds'>,
): boolean => {
  return Array.isArray(product.linkedProductTagIds) && product.linkedProductTagIds.length > 0;
};
